import {
  effectiveSelections,
  materialsForZone,
  selectableMaterials,
  type KitchenCatalog,
  type KitchenConfig,
  type KitchenMaterial,
  type Lighting,
  type ZoneKey,
} from '@cfg/shared';

// the availability rules live in @cfg/shared, so the API's PDF export resolves them the same way
export { effectiveSelections, selectableMaterials };

/** A valid starting configuration (first scene, its defaults). */
export function initialConfig(catalog: KitchenCatalog): KitchenConfig {
  return { sceneKey: catalog.scenes[0]!.key, lighting: 'day', selections: {} };
}

/** Drops selections that are no longer valid in the current catalog. */
export function sanitizeConfig(catalog: KitchenCatalog, config: KitchenConfig): KitchenConfig {
  const scene = catalog.scenes.find((s) => s.key === config.sceneKey) ?? catalog.scenes[0]!;
  const selections: KitchenConfig['selections'] = {};
  for (const [key, id] of Object.entries(config.selections) as [ZoneKey, string][]) {
    const zone = catalog.zones.find((z) => z.key === key);
    if (zone && selectableMaterials(catalog, zone).some((m) => m.id === id)) {
      selections[key] = id;
    }
  }
  const lighting: Lighting = scene.manifest.lightings[config.lighting] ? config.lighting : 'day';
  return { sceneKey: scene.key, lighting, selections };
}

/**
 * URL format (readable, stable across catalog versions because it uses material codes):
 *   ?scene=kitchen-island&light=night&m=upper_cabinets:LAC-WHT-M,floor:FLR-OAK-NAT
 */
export function configToSearch(catalog: KitchenCatalog, config: KitchenConfig): string {
  const params = new URLSearchParams(location.search);
  params.delete('c');
  params.set('scene', config.sceneKey);
  params.set('light', config.lighting);
  const byId = new Map(catalog.materials.map((m) => [m.id, m.code]));
  const pairs = Object.entries(config.selections)
    .map(([zone, id]) => (byId.get(id) ? `${zone}:${byId.get(id)}` : null))
    .filter(Boolean);
  if (pairs.length) params.set('m', pairs.join(','));
  else params.delete('m');
  return `?${params.toString()}`;
}

export function configFromSearch(catalog: KitchenCatalog, search: string): KitchenConfig | null {
  const params = new URLSearchParams(search);
  const scene = params.get('scene');
  if (!scene) return null;
  const byCode = new Map(catalog.materials.map((m) => [m.code, m.id]));
  const selections: KitchenConfig['selections'] = {};
  for (const pair of (params.get('m') ?? '').split(',')) {
    const [zone, code] = pair.split(':');
    const id = code ? byCode.get(code) : undefined;
    if (zone && id) selections[zone as ZoneKey] = id;
  }
  return sanitizeConfig(catalog, {
    sceneKey: scene,
    lighting: params.get('light') === 'night' ? 'night' : 'day',
    selections,
  });
}

export function zoneMaterials(catalog: KitchenCatalog, zone: ZoneKey): KitchenMaterial[] {
  return materialsForZone(catalog, zone);
}
